import React, { useEffect } from 'react';

const siteUrl = 'https://www.redrender.in';
const siteName = 'RedRender';
const defaultImage = `${siteUrl}/logo.png`;

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article' | 'profile';
  keywords?: string[];
  noIndex?: boolean;
  jsonLd?: JsonLd;
}

const absoluteUrl = (value: string) => {
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  return `${siteUrl}${value.startsWith('/') ? value : `/${value}`}`;
};

const setMetaByName = (name: string, content: string) => {
  let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const setMetaByProperty = (property: string, content: string) => {
  let element = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const setCanonical = (url: string) => {
  let element = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);
};

const setJsonLd = (data?: JsonLd) => {
  const existing = document.querySelector<HTMLScriptElement>('script[data-seo-json-ld="true"]');
  existing?.remove();

  if (!data) {
    return;
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.seoJsonLd = 'true';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = '/',
  image = defaultImage,
  type = 'website',
  keywords,
  noIndex = false,
  jsonLd
}) => {
  useEffect(() => {
    const pageUrl = absoluteUrl(path);
    const imageUrl = absoluteUrl(image);

    document.title = title;

    setMetaByName('description', description);
    setMetaByName('robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    setMetaByName('author', 'RedRender');
    setMetaByName('theme-color', '#ff5555');

    if (keywords?.length) {
      setMetaByName('keywords', keywords.join(', '));
    }

    setCanonical(pageUrl);

    setMetaByProperty('og:site_name', siteName);
    setMetaByProperty('og:title', title);
    setMetaByProperty('og:description', description);
    setMetaByProperty('og:type', type);
    setMetaByProperty('og:url', pageUrl);
    setMetaByProperty('og:image', imageUrl);

    setMetaByName('twitter:card', 'summary_large_image');
    setMetaByName('twitter:title', title);
    setMetaByName('twitter:description', description);
    setMetaByName('twitter:image', imageUrl);

    setJsonLd(jsonLd);
  }, [description, image, jsonLd, keywords, noIndex, path, title, type]);

  return null;
};

export default SEO;
export { siteUrl, siteName };
